import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Zap, Clock } from "lucide-react";
import { API, axios } from "@/lib/api";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, delay: 0.35 + i * 0.12, ease: [0.22, 1, 0.36, 1] } }),
};

const Reveal = ({ children, i }) => (
  <span className="block overflow-hidden">
    <motion.span variants={line} custom={i} initial="hidden" animate="show" className="block">
      {children}
    </motion.span>
  </span>
);

const go = (href) => document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });

const Hero = () => {
  const [capacity, setCapacity] = useState(null);

  useEffect(() => {
    axios.get(`${API}/capacity/launch`).then(({ data }) => setCapacity(data)).catch(() => {});
  }, []);

  return (
    <section className="relative min-h-screen flex items-end overflow-hidden pt-28 pb-16 md:pb-24" data-testid="hero">
      {/* background glow + parallax abstract */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 -right-40 w-[55vw] h-[55vw] rounded-full bg-[#ccff00]/10 blur-[120px]" />
        <div className="absolute bottom-0 -left-40 w-[45vw] h-[45vw] rounded-full bg-[#ff0055]/10 blur-[120px]" />
        <motion.img
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 0.35 }}
          transition={{ duration: 1.6, ease: "easeOut" }}
          src="https://images.unsplash.com/photo-1633218388467-539651dcf81f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwzfHwzZCUyMGFic3RyYWN0JTIwc2hhcGVzJTIwZGFyayUyMG5lb258ZW58MHx8fHwxNzg3ODU5OTQ0fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute top-0 right-0 w-[60vw] max-w-3xl object-cover mix-blend-lighten"
        />
      </div>

      <div className="max-w-7xl mx-auto px-5 md:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="inline-flex items-center gap-2 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/40 text-[#ff0055] text-xs font-semibold uppercase tracking-widest px-4 py-2 mb-8"
          data-testid="hero-badge"
        >
          <Clock size={14} />
          <span data-testid="hero-slot-counter">
            {capacity?.full
              ? "Today's 48h slots are full → next slot tomorrow"
              : capacity
              ? `Order today → live in 48 hours · ${capacity.slots_left}/${capacity.cap} slots left today`
              : "Order today → live in 48 hours"}
          </span>
        </motion.div>

        <h1 className="font-display font-black tracking-tighter leading-[0.92] text-5xl sm:text-6xl md:text-7xl lg:text-[6.2rem]" data-testid="hero-heading">
          <Reveal i={0}>Your business</Reveal>
          <Reveal i={1}>
            online <span className="text-[#ccff00]">by Friday.</span>
          </Reveal>
        </h1>

        <div className="grid lg:grid-cols-[1.4fr_1fr] gap-8 lg:gap-16 mt-8 md:mt-12 items-end">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="text-lg md:text-xl text-zinc-300 max-w-2xl leading-relaxed"
          >
            Your business deserves more than an Instagram page. We get your{" "}
            <span className="text-white font-medium">brand, website and entire online presence</span> sorted —
            without the agency price tag or the six-week wait. Fast. Professional. Done for you.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="flex flex-col sm:flex-row lg:flex-col gap-3"
          >
            <button onClick={() => go("#pricing")} className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#ccff00] text-black font-bold uppercase tracking-widest text-sm px-7 py-4 hover:scale-105 transition-transform" data-testid="hero-cta-primary">
              Launch my business <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => go("#score")} className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white font-medium text-sm px-7 py-4 hover:bg-white/5 transition-colors" data-testid="hero-cta-secondary">
              <Zap size={16} className="text-[#ccff00]" /> Free Website Score
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.3 }}
          className="mt-14 flex flex-wrap gap-x-8 gap-y-3 text-sm text-zinc-500"
          data-testid="hero-trust"
        >
          <span>★★★★★ Loved by UK small businesses</span>
          <span className="hidden sm:inline">Human quality-checked</span>
          <span>No lock-in · from £199</span>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
